import React, { useRef, useState } from 'react'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { toast } from '~/client/src/shared/utils/toast'

import { buildTemplatePropertyInputs } from './formProperties'
import type { IFormPropertiesShellSubmit } from './FormPropertiesShell'
import { FormPropertiesShell } from './FormPropertiesShell'
import { useFormPropertiesData } from './useFormPropertiesData'

interface IProps {
  templateId: string
  resourceTypeId: string
  onClose: () => void
  onBackToPicker?: () => void
  resourceSchema: ResourceSchema
}

export const EditFormSidebar: React.FC<IProps> = ({
  templateId,
  resourceTypeId,
  onClose,
  onBackToPicker,
  resourceSchema,
}) => {
  const { templateName, properties, savedProperties, loading } =
    useFormPropertiesData({
      templateId,
      resourceTypeId,
      resourceSchema,
    })

  const [saving, setSaving] = useState(false)

  const hasLoadedOnceRef = useRef(false)
  if (!loading) hasLoadedOnceRef.current = true
  const shellKey = hasLoadedOnceRef.current ? 'ready' : 'loading'

  const handleSave = async ({
    name,
    properties: propertyEdits,
  }: IFormPropertiesShellSubmit) => {
    setSaving(true)
    try {
      const input = {
        name,
        properties: buildTemplatePropertyInputs(propertyEdits, savedProperties),
      }
      await resourceSchema.updateForm(templateId, input)
      onClose()
    } catch (error) {
      toast.error(Localization.translator.failedToSaveChangesTryAgain)
      console.error(error)
      setSaving(false)
    }
  }

  return (
    <FormPropertiesShell
      key={shellKey}
      headerTitle={Localization.translator.formProperties}
      submitLabel={Localization.translator.save}
      resourceTypeId={resourceTypeId}
      properties={properties}
      loading={loading}
      saving={saving}
      initialName={templateName}
      autoFocusName={false}
      onClose={onClose}
      onBackToPicker={onBackToPicker}
      onSubmit={handleSave}
    />
  )
}
